import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, FileText, RefreshCw, Wallet } from "lucide-react";
import AdminLayout from "layouts/AdminLayout";
import AdminCard from "components/admin/AdminCard";
import AdminStatCard from "components/admin/AdminStatCard";
import StatusBadge from "components/admin/StatusBadge";
import Pagination from "components/Pagination";
import { Button } from "components/ui/button";
import {
  BoletoAdmin,
  RecargaComCliente,
  listBoletos,
  listRecargas,
  sincronizarBoleto,
  sincronizarRecarga,
} from "services/admin/adminCarteira.service";

const SELECT_CLASS = "h-9 rounded-md border border-input bg-background px-3 text-sm";

const TOM_STATUS: Record<string, "green" | "yellow" | "red" | "slate"> = {
  PAGO: "green",
  PENDING: "yellow",
  ESTORNADO: "red",
  FALHOU: "red",
  CANCELADO: "slate",
};

// Status crus do Asaas. Os tres primeiros sao os que situacaoPagamento aceita
// como pago e que liberam a remessa.
const TOM_BOLETO: Record<string, "green" | "yellow" | "red" | "slate"> = {
  RECEIVED: "green",
  CONFIRMED: "green",
  RECEIVED_IN_CASH: "green",
  PENDING: "yellow",
  OVERDUE: "red",
  REFUNDED: "red",
  DELETED: "slate",
};

const BOLETO_PAGO = ["RECEIVED", "CONFIRMED", "RECEIVED_IN_CASH"];

const ROTULO_METODO: Record<string, string> = {
  PIX: "PIX",
  CREDIT_CARD: "Cartão",
};

const brl = (v: number) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const quando = (iso: string | null) => (iso ? new Date(iso).toLocaleString("pt-BR") : "—");

// dueDate é DATEONLY ("2026-10-30"): sem a hora o JS lê como UTC e, no fuso do
// Brasil, a data aparece um dia antes.
const dataCurta = (iso: string | null) =>
  iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("pt-BR") : "—";

const horasAtras = (iso: string) => (Date.now() - new Date(iso).getTime()) / 3600000;

const mensagemErro = (err: unknown, padrao: string) =>
  (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
  (err as Error)?.message ||
  padrao;

const AdminCarteira = () => {
  const navigate = useNavigate();

  // Duas naturezas diferentes na mesma tela: recarga entra como saldo na
  // carteira, boleto paga a remessa direto. Aba em vez de duas páginas porque
  // a pergunta do atendente é a mesma — "esse pagamento caiu?".
  const [aba, setAba] = useState<"recargas" | "boletos">("recargas");

  const [recargas, setRecargas] = useState<RecargaComCliente[]>([]);
  const [pendentesAntigos, setPendentesAntigos] = useState(0);
  const [boletos, setBoletos] = useState<BoletoAdmin[]>([]);
  const [boletosVencidos, setBoletosVencidos] = useState(0);

  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [status, setStatus] = useState("");
  const [metodo, setMetodo] = useState("");
  const [sincronizando, setSincronizando] = useState<number | null>(null);

  const carregar = () => {
    setLoading(true);
    setErro(null);

    const pedido =
      aba === "recargas"
        ? listRecargas({ page, pageSize: 25, status, metodo }).then((r) => {
            setRecargas(r.recargas);
            setPendentesAntigos(r.pendentes_antigos);
            return r;
          })
        : listBoletos({ page, pageSize: 25, status }).then((r) => {
            setBoletos(r.boletos);
            setBoletosVencidos(r.pendentes_vencidos);
            return r;
          });

    pedido
      .then((r) => {
        setTotal(r.total);
        setTotalPages(Math.max(1, Math.ceil(r.total / r.pageSize)));
      })
      .catch((err) => {
        console.error("[AdminCarteira] erro:", err);
        setErro(
          mensagemErro(err, "Não foi possível carregar. Confira se o backend está publicado.")
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(carregar, [aba, page, status, metodo]);

  const trocarAba = (nova: "recargas" | "boletos") => {
    // Os filtros de status não são os mesmos nas duas abas (PAGO/PENDING de um
    // lado, RECEIVED/OVERDUE do outro): manter o valor antigo filtraria por
    // algo que não existe e a lista viria vazia sem explicação.
    setAba(nova);
    setStatus("");
    setMetodo("");
    setPage(1);
    setAviso(null);
    setErro(null);
  };

  const sincronizarRecargaLinha = async (id: number) => {
    setSincronizando(id);
    setAviso(null);
    setErro(null);
    try {
      const r = await sincronizarRecarga(id);
      setAviso(
        r.mudou
          ? `Recarga #${id}: ${r.status_anterior} → ${r.recarga.status}. Saldo do cliente: ${brl(r.saldo)}.`
          : `Recarga #${id} segue como ${r.recarga.status} no Asaas. Nada mudou.`
      );
      carregar();
    } catch (err) {
      console.error("[AdminCarteira] erro ao sincronizar recarga:", err);
      setErro(mensagemErro(err, "Falha ao consultar o Asaas."));
    } finally {
      setSincronizando(null);
    }
  };

  const sincronizarBoletoLinha = async (id: number) => {
    setSincronizando(id);
    setAviso(null);
    setErro(null);
    try {
      const r = await sincronizarBoleto(id);
      setAviso(
        r.mudou
          ? `Boleto #${id}: ${r.status_anterior ?? "—"} → ${r.boleto.status}.` +
              (r.remessa_paga ? " A remessa consta como paga." : "")
          : `Boleto #${id} segue como ${r.boleto.status} no Asaas. Nada mudou.`
      );
      carregar();
    } catch (err) {
      console.error("[AdminCarteira] erro ao sincronizar boleto:", err);
      setErro(mensagemErro(err, "Falha ao consultar o Asaas."));
    } finally {
      setSincronizando(null);
    }
  };

  const abaClass = (nome: "recargas" | "boletos") =>
    `px-3 py-1.5 rounded-md text-sm font-medium transition ${
      aba === nome ? "bg-brand-sidebar text-white" : "text-slate-600 hover:bg-slate-100"
    }`;

  return (
    <AdminLayout title="Carteira" subtitle="Recargas, boletos e conciliação com o Asaas">
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <AdminStatCard
            label={aba === "recargas" ? "Recargas no filtro" : "Boletos no filtro"}
            value={total}
            icon={aba === "recargas" ? <Wallet /> : <FileText />}
          />
          <AdminStatCard
            label="Recargas pendentes > 1h"
            value={pendentesAntigos}
            icon={<AlertTriangle />}
            tone={pendentesAntigos > 0 ? "danger" : "default"}
          />
          <AdminStatCard
            label="Boletos vencidos sem baixa"
            value={boletosVencidos}
            icon={<AlertTriangle />}
            tone={boletosVencidos > 0 ? "danger" : "default"}
          />
        </div>

        <div className="flex items-center gap-1 bg-white rounded-xl border border-slate-200/80 p-1 w-fit">
          <button type="button" className={abaClass("recargas")} onClick={() => trocarAba("recargas")}>
            Recargas
          </button>
          <button type="button" className={abaClass("boletos")} onClick={() => trocarAba("boletos")}>
            Boletos
          </button>
        </div>

        {aba === "recargas" && pendentesAntigos > 0 ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Recarga pendente há mais de uma hora costuma ser webhook do Asaas que não
            chegou. Use <strong>Sincronizar</strong> na linha: o sistema relê o status no
            Asaas e credita o saldo se já foi pago.
          </div>
        ) : null}

        {aba === "boletos" ? (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            O status do boleto só era atualizado pelo webhook do Asaas. Se o webhook não
            estiver cadastrado, falhar ou se perder, a remessa fica como não paga para
            sempre. <strong>Sincronizar</strong> relê o status direto no Asaas — não
            inventa pagamento, só copia o que ele responde.
          </div>
        ) : null}

        {aviso ? (
          <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            {aviso}
          </div>
        ) : null}
        {erro ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {erro}
          </div>
        ) : null}

        <AdminCard noPadding className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <select
              className={SELECT_CLASS}
              value={status}
              onChange={(e) => {
                setPage(1);
                setStatus(e.target.value);
              }}
            >
              <option value="">Todos os status</option>
              {aba === "recargas" ? (
                <>
                  <option value="PENDING">Pendente</option>
                  <option value="PAGO">Pago</option>
                  <option value="ESTORNADO">Estornado</option>
                  <option value="FALHOU">Falhou</option>
                  <option value="CANCELADO">Cancelado</option>
                </>
              ) : (
                <>
                  <option value="PENDING">Pendente</option>
                  <option value="RECEIVED">Recebido</option>
                  <option value="CONFIRMED">Confirmado</option>
                  <option value="RECEIVED_IN_CASH">Recebido em dinheiro</option>
                  <option value="OVERDUE">Vencido</option>
                  <option value="REFUNDED">Estornado</option>
                </>
              )}
            </select>

            {aba === "recargas" ? (
              <select
                className={SELECT_CLASS}
                value={metodo}
                onChange={(e) => {
                  setPage(1);
                  setMetodo(e.target.value);
                }}
              >
                <option value="">Todos os métodos</option>
                <option value="PIX">PIX</option>
                <option value="CREDIT_CARD">Cartão</option>
              </select>
            ) : null}

            <Button variant="outline" size="sm" onClick={carregar} disabled={loading}>
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              Atualizar
            </Button>
          </div>
        </AdminCard>

        <AdminCard noPadding className="p-4">
          {loading ? (
            <p className="text-sm text-muted-foreground">Carregando...</p>
          ) : aba === "recargas" ? (
            recargas.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhuma recarga com esses filtros.</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                        <th className="py-2 pr-4 font-semibold">Cliente</th>
                        <th className="py-2 pr-4 font-semibold">Método</th>
                        <th className="py-2 pr-4 font-semibold text-right">Valor</th>
                        <th className="py-2 pr-4 font-semibold text-right">Total pago</th>
                        <th className="py-2 pr-4 font-semibold">Status</th>
                        <th className="py-2 pr-4 font-semibold">Criada</th>
                        <th className="py-2 font-semibold" />
                      </tr>
                    </thead>
                    <tbody>
                      {recargas.map((r) => {
                        const travada = r.status === "PENDING" && horasAtras(r.criado_em) > 1;
                        return (
                          <tr key={r.id} className="border-t border-slate-100 align-top">
                            <td className="py-3 pr-4">
                              <button
                                type="button"
                                className="text-slate-900 font-medium hover:underline text-left"
                                onClick={() => navigate(`/admin/clientes/${r.cliente.id}`)}
                              >
                                {r.cliente.razaoSocial ?? `Cliente #${r.cliente.id}`}
                              </button>
                              <p className="text-[12px] text-slate-500">
                                #{r.id} · {r.cliente.emailPrincipal ?? ""}
                              </p>
                            </td>
                            <td className="py-3 pr-4">{ROTULO_METODO[r.metodo] ?? r.metodo}</td>
                            <td className="py-3 pr-4 text-right tabular-nums">{brl(r.valor)}</td>
                            <td className="py-3 pr-4 text-right tabular-nums">
                              {brl(r.total)}
                              {r.taxa > 0 ? (
                                <p className="text-[12px] text-slate-500">taxa {brl(r.taxa)}</p>
                              ) : null}
                            </td>
                            <td className="py-3 pr-4">
                              <StatusBadge label={r.status} tone={TOM_STATUS[r.status] ?? "slate"} />
                              {travada ? (
                                <p className="text-[12px] text-amber-700 mt-1">parada &gt; 1h</p>
                              ) : null}
                            </td>
                            <td className="py-3 pr-4 whitespace-nowrap text-[12px] text-slate-600">
                              {quando(r.criado_em)}
                              {r.pago_em ? (
                                <p className="text-green-700">pago {quando(r.pago_em)}</p>
                              ) : null}
                            </td>
                            <td className="py-3 whitespace-nowrap">
                              {r.status === "PENDING" ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={sincronizando === r.id}
                                  onClick={() => sincronizarRecargaLinha(r.id)}
                                >
                                  {sincronizando === r.id ? "..." : "Sincronizar"}
                                </Button>
                              ) : null}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Pagination page={page} totalPages={totalPages} onChange={setPage} />
              </>
            )
          ) : boletos.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum boleto com esses filtros.</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                      <th className="py-2 pr-4 font-semibold">Cliente</th>
                      <th className="py-2 pr-4 font-semibold">Cobre</th>
                      <th className="py-2 pr-4 font-semibold text-right">Valor</th>
                      <th className="py-2 pr-4 font-semibold">Vencimento</th>
                      <th className="py-2 pr-4 font-semibold">Status</th>
                      <th className="py-2 font-semibold" />
                    </tr>
                  </thead>
                  <tbody>
                    {boletos.map((b) => {
                      const pago = BOLETO_PAGO.includes(String(b.status));
                      const vencido =
                        b.status === "PENDING" &&
                        !!b.vencimento &&
                        new Date(`${b.vencimento}T23:59:59`) < new Date();
                      return (
                        <tr key={b.id} className="border-t border-slate-100 align-top">
                          <td className="py-3 pr-4">
                            <button
                              type="button"
                              className="text-slate-900 font-medium hover:underline text-left"
                              onClick={() => navigate(`/admin/clientes/${b.cliente_id}`)}
                            >
                              {b.cliente?.razaoSocial ?? `Cliente #${b.cliente_id}`}
                            </button>
                            <p className="text-[12px] text-slate-500">
                              #{b.id} · {b.cliente?.emailPrincipal ?? ""}
                            </p>
                          </td>
                          <td className="py-3 pr-4">
                            {b.envio ? (
                              <>
                                <p className="text-slate-800">{b.envio.pedido_ref}</p>
                                <p className="text-[12px] text-slate-500">
                                  {b.envio.carrier}
                                  {b.envio.status_norm ? ` · ${b.envio.status_norm}` : ""}
                                </p>
                              </>
                            ) : (
                              <span className="text-[12px] text-slate-500">
                                lote (várias cotações)
                              </span>
                            )}
                          </td>
                          <td className="py-3 pr-4 text-right tabular-nums">{brl(b.valor)}</td>
                          <td className="py-3 pr-4 whitespace-nowrap">
                            <span className={vencido ? "text-red-600 font-medium" : "text-slate-700"}>
                              {dataCurta(b.vencimento)}
                            </span>
                            <p className="text-[12px] text-slate-500">
                              emitido {quando(b.criado_em)}
                            </p>
                          </td>
                          <td className="py-3 pr-4">
                            <StatusBadge
                              label={b.status ?? "—"}
                              tone={TOM_BOLETO[String(b.status)] ?? "slate"}
                            />
                            {pago && b.cotacao_id ? (
                              <p className="text-[12px] text-green-700 mt-1">remessa paga</p>
                            ) : null}
                            {vencido ? (
                              <p className="text-[12px] text-red-600 mt-1">vencido sem baixa</p>
                            ) : null}
                          </td>
                          <td className="py-3 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={sincronizando === b.id}
                                onClick={() => sincronizarBoletoLinha(b.id)}
                              >
                                {sincronizando === b.id ? "..." : "Sincronizar"}
                              </Button>
                              {b.bank_slip_url ? (
                                <a
                                  href={b.bank_slip_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-[13px] text-blue-700 hover:underline"
                                >
                                  Abrir
                                </a>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Pagination page={page} totalPages={totalPages} onChange={setPage} />
            </>
          )}
        </AdminCard>

        <p className="text-[12px] text-slate-500">
          Saldo, extrato e ajuste manual de um cliente ficam na página dele, em
          Clientes → abrir o cliente → Carteira.
        </p>
      </div>
    </AdminLayout>
  );
};

export default AdminCarteira;
